import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AppLayout from '../components/layout/AppLayout';
import RadarCompareChart from '../components/charts/RadarCompareChart';
import { useAuth } from '../contexts/AuthContext';
import {
    Activity,
    Users,
    Swords,
    History,
    Star,
    Diamond,
    Sparkles,
    Clock,
    Tag,
    ChevronRight,
    Link as LinkIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

interface ComparisonData {
    id: string;
    name: string;
    brandName?: string;
    avgRating: number;
    avgValue: number;
    sessionCount: number;
    effects: {
        anxiety: number;
        bodyHigh: number;
        headHigh: number;
        creativity: number;
        smoothness: number;
    };
    color: string;
}

export default function Compare() {
    const { user } = useAuth();
    const [view, setView] = useState<'feed' | 'battle'>('feed');
    const [products, setProducts] = useState<any[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
    const [recentSessions, setRecentSessions] = useState<any[]>([]);
    const [hasPartner, setHasPartner] = useState(false);
    const [loading, setLoading] = useState(true);

    const colors = ['#8B5CF6', '#10B981', '#3B82F6', '#F59E0B'];

    useEffect(() => {
        fetchInitialData();
    }, [user]);

    useEffect(() => {
        if (selectedProductIds.length > 0) {
            fetchComparisonData();
        } else {
            setComparisonData([]);
        }
    }, [selectedProductIds]);

    const fetchInitialData = async () => {
        if (!user) return;
        setLoading(true);

        // Check for partner
        const { data: partnerData } = await supabase
            .from('user_links')
            .select('partner_id')
            .eq('user_id', user.id)
            .maybeSingle();

        setHasPartner(!!partnerData);

        // Fetch products with brand and strain names
        const { data: productsData } = await supabase
            .from('products')
            .select(`
                id,
                label_name,
                product_type,
                brands(name),
                strains(name)
            `)
            .order('label_name');

        if (productsData) setProducts(productsData);

        // Fetch recent sessions for feed (RLS will now include partner's)
        const { data: sessionsData } = await supabase
            .from('session_details')
            .select('*')
            .order('date_used', { ascending: false })
            .limit(20);

        if (sessionsData) setRecentSessions(sessionsData);
        setLoading(false);
    };

    const fetchComparisonData = async () => {
        const data: ComparisonData[] = [];

        for (let i = 0; i < selectedProductIds.length; i++) {
            const id = selectedProductIds[i];
            const { data: sessions } = await supabase
                .from('session_details')
                .select('*')
                .eq('product_id', id);

            if (sessions && sessions.length > 0) {
                const product = products.find(p => p.id === id);
                const displayName = product?.label_name || product?.strains?.name || 'Unknown';
                const brandName = product?.brands?.name;
                const count = sessions.length;

                const avgRating = sessions.reduce((acc, s) => acc + s.overall_rating, 0) / count;
                const avgValue = sessions.reduce((acc, s) => acc + s.value_score, 0) / count;

                const avgEffects = {
                    anxiety: sessions.reduce((acc, s) => acc + s.anxiety, 0) / count,
                    bodyHigh: sessions.reduce((acc, s) => acc + s.body_high, 0) / count,
                    headHigh: sessions.reduce((acc, s) => acc + s.head_high, 0) / count,
                    creativity: sessions.reduce((acc, s) => acc + s.creativity, 0) / count,
                    smoothness: sessions.reduce((acc, s) => acc + s.smoothness, 0) / count,
                };

                data.push({
                    id,
                    name: displayName,
                    brandName: brandName,
                    avgRating,
                    avgValue,
                    sessionCount: count,
                    effects: avgEffects,
                    color: colors[i % colors.length]
                });
            }
        }
        setComparisonData(data);
    };

    const toggleProduct = (id: string) => {
        if (selectedProductIds.includes(id)) {
            setSelectedProductIds(selectedProductIds.filter(i => i !== id));
        } else if (selectedProductIds.length < 4) {
            setSelectedProductIds([...selectedProductIds, id]);
        }
    };

    // Prepare data for radar chart
    const radarData = [
        { subject: 'Anxiety' },
        { subject: 'Body' },
        { subject: 'Head' },
        { subject: 'Creativity' },
        { subject: 'Smoothness' },
    ].map(row => {
        const newRow: any = { ...row };
        comparisonData.forEach(p => {
            const key = row.subject === 'Body' ? 'bodyHigh' : row.subject === 'Head' ? 'headHigh' : row.subject.toLowerCase();
            newRow[p.id] = Number(p.effects[key as keyof typeof p.effects].toFixed(1));
        });
        return newRow;
    });

    const radarSubjects = comparisonData.map(p => ({
        key: p.id,
        name: p.brandName ? `${p.brandName} - ${p.name}` : p.name,
        color: p.color
    }));

    if (loading) {
        return (
            <AppLayout>
                <div className="flex flex-col items-center justify-center min-h-[60vh] animate-pulse">
                    <div className="bg-accent-purple/10 p-4 rounded-full mb-6">
                        <Users className="text-accent-purple" size={40} />
                    </div>
                    <p className="text-dark-muted font-black uppercase tracking-[0.3em] text-[10px]">Synchronizing Duo Network</p>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="space-y-6 pb-24">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-black italic tracking-tight">Duo<span className="text-accent-purple">Hub</span></h1>
                        <p className="text-[10px] font-black uppercase tracking-widest text-dark-muted">Linked shared ecosystem</p>
                    </div>
                    {!hasPartner && (
                        <Link to="/settings" className="bg-amber-500/10 border border-amber-500/20 text-amber-500 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-amber-500/20 transition-all">
                            <LinkIcon size={12} strokeWidth={3} /> Link Duo
                        </Link>
                    )}
                </div>

                {/* View Switcher Tabs */}
                <div className="flex p-1.5 bg-dark-surface/40 backdrop-blur-md border border-white/5 rounded-2xl sticky top-20 z-30 shadow-2xl">
                    <button
                        onClick={() => setView('feed')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-[0.15em] rounded-xl transition-all duration-300 ${view === 'feed'
                            ? 'bg-accent-purple text-white shadow-lg shadow-accent-purple/30 active:scale-95'
                            : 'text-dark-muted hover:text-white'
                            }`}
                    >
                        <History size={14} /> Shared Feed
                    </button>
                    <button
                        onClick={() => setView('battle')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-[0.15em] rounded-xl transition-all duration-300 ${view === 'battle'
                            ? 'bg-accent-purple text-white shadow-lg shadow-accent-purple/30 active:scale-95'
                            : 'text-dark-muted hover:text-white'
                            }`}
                    >
                        <Swords size={14} /> Battle Mode
                    </button>
                </div>

                {view === 'feed' ? (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {recentSessions.length === 0 ? (
                            <div className="card text-center py-20 bg-dark-surface/30 border-dashed border-white/10 opacity-60">
                                <Activity size={32} className="text-dark-muted opacity-20 mx-auto mb-4" />
                                <h3 className="font-black text-sm uppercase tracking-widest">Atmospheric Silence</h3>
                                <p className="text-[10px] text-dark-muted max-w-[200px] mx-auto mt-2 font-medium">Log a session to begin your shared legacy timeline.</p>
                            </div>
                        ) : (
                            recentSessions.map((session) => {
                                const isSelf = session.user_id === user?.id;
                                const initials = session.user_display_name ? session.user_display_name.substring(0, 2).toUpperCase() : (isSelf ? 'ME' : 'DU');

                                return (
                                    <div key={session.id} className="relative group scroll-m-24">
                                        <div className="flex items-center gap-2 mb-2 ml-1">
                                            <div className={`w-1.5 h-1.5 rounded-full ${isSelf ? 'bg-accent-purple shadow-[0_0_8px_rgba(168,85,247,0.5)]' : 'bg-accent-blue shadow-[0_0_8px_rgba(59,130,246,0.5)]'}`} />
                                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-dark-muted">
                                                {isSelf ? 'Your Entry' : `${session.user_display_name || 'Partner'}'s Update`}
                                            </p>
                                        </div>

                                        <div className="card bg-dark-surface/30 backdrop-blur-md hover:bg-dark-surface/50 border-white/5 transition-all duration-500 cursor-default group-hover:border-white/10">
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-black text-sm border shadow-2xl transition-transform group-hover:scale-105 ${isSelf
                                                    ? 'bg-accent-purple/20 border-accent-purple/30 text-accent-purple'
                                                    : 'bg-accent-blue/20 border-accent-blue/30 text-accent-blue'
                                                    }`}>
                                                    {initials}
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-black text-lg tracking-tight truncate leading-tight italic uppercase">
                                                            {session.strain_name || 'Mystery Strain'}
                                                        </h4>
                                                        <div className="flex items-center gap-1.5 bg-accent-yellow/10 border border-accent-yellow/20 px-2 py-0.5 rounded-lg shrink-0 shadow-inner">
                                                            <Star size={10} fill="currentColor" className="text-accent-yellow" />
                                                            <span className="text-[11px] font-black text-accent-yellow">{session.overall_rating}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <div className="flex items-center gap-1.5 text-dark-muted/60">
                                                            <Clock size={10} strokeWidth={3} />
                                                            <span className="text-[9px] font-black uppercase tracking-tighter">{format(new Date(session.date_used), 'MMM d • h:mm a')}</span>
                                                        </div>
                                                        {session.brand_name && (
                                                            <span className="text-[9px] font-black uppercase tracking-tight bg-dark-bg/60 text-dark-muted px-2 py-0.5 rounded-lg border border-white/5">
                                                                {session.brand_name}
                                                            </span>
                                                        )}
                                                        <span className={`text-[9px] font-black uppercase tracking-tight px-2 py-0.5 rounded-lg border backdrop-blur-sm ${session.value_score >= 50
                                                            ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                                            : session.value_score >= 25
                                                                ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                                                                : 'bg-red-500/10 border-red-500/20 text-red-400'
                                                            }`}>
                                                            V-Index {Math.round(session.value_score)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {session.notes && (
                                                <div className="mb-5 bg-dark-bg/40 p-4 rounded-2xl border border-white/5 shadow-inner">
                                                    <p className="text-[12px] font-bold italic text-dark-text/90 leading-relaxed">
                                                        "{session.notes}"
                                                    </p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-5 gap-4 pt-4 border-t border-white/5">
                                                {[
                                                    { label: 'ANX', val: session.anxiety },
                                                    { label: 'BODY', val: session.body_high },
                                                    { label: 'HEAD', val: session.head_high },
                                                    { label: 'CREA', val: session.creativity },
                                                    { label: 'SMO', val: session.smoothness }
                                                ].map((eff) => (
                                                    <div key={eff.label} className="flex flex-col gap-2.5">
                                                        <span className="text-[7px] font-black text-dark-muted uppercase tracking-[0.2em] text-center truncate opacity-60">
                                                            {eff.label}
                                                        </span>
                                                        <div className="h-1 bg-dark-bg/80 rounded-full overflow-hidden shadow-inner flex-1">
                                                            <div
                                                                className={`h-full transition-all duration-1000 shadow-[0_0_8px_currentColor] ${isSelf ? 'bg-accent-purple text-accent-purple' : 'bg-accent-blue text-accent-blue'}`}
                                                                style={{ width: `${(eff.val || 0) * 10}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                ) : (
                    <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                        {/* Battle Mode Selection */}
                        <div className="card bg-dark-surface/40 backdrop-blur-md border-white/5 space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-accent-purple/10 rounded-lg">
                                        <Tag size={14} className="text-accent-purple" />
                                    </div>
                                    <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">Registry</h3>
                                </div>
                                <span className="text-[10px] bg-dark-bg px-3 py-1 rounded-full border border-white/5 font-black text-accent-purple uppercase tracking-[0.1em]">
                                    {selectedProductIds.length}/4 Matrix
                                </span>
                            </div>

                            <div className="max-h-[220px] overflow-y-auto pr-3 space-y-2.5 scrollbar-hide">
                                {products.map((p) => {
                                    const isSelected = selectedProductIds.includes(p.id);
                                    return (
                                        <button
                                            key={p.id}
                                            onClick={() => toggleProduct(p.id)}
                                            className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 active:scale-[0.98] ${isSelected
                                                ? 'bg-accent-purple border-accent-purple/50 text-white shadow-xl shadow-accent-purple/20'
                                                : 'bg-dark-bg/40 border-white/5 text-dark-muted hover:border-accent-purple/30 group'
                                                }`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2 rounded-xl transition-colors ${isSelected ? 'bg-white/20' : 'bg-dark-surface border border-white/5 group-hover:bg-accent-purple/5'}`}>
                                                    <Diamond size={12} className={isSelected ? 'text-white' : 'text-accent-purple/40'} />
                                                </div>
                                                <div className="text-left">
                                                    <p className="text-[11px] font-black uppercase tracking-tight leading-none truncate max-w-[160px]">
                                                        {p.label_name || p.strains?.name}
                                                    </p>
                                                    {p.brands?.name && (
                                                        <p className={`text-[9px] font-black uppercase tracking-widest mt-1.5 ${isSelected ? 'text-white/60' : 'text-accent-purple/40'}`}>
                                                            {p.brands.name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight size={14} className={`transition-transform duration-500 ${isSelected ? 'rotate-90 text-white' : 'opacity-20 translate-x-1'}`} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedProductIds.length > 0 ? (
                            <div className="space-y-6 animate-in zoom-in-95 duration-500">
                                <div className="card relative overflow-hidden bg-dark-surface/40 backdrop-blur-md border-white/5">
                                    <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none rotate-12 scale-150">
                                        <Swords size={200} />
                                    </div>

                                    <div className="flex items-center justify-center gap-3 mb-10 relative z-10">
                                        <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10" />
                                        <h2 className="text-sm font-black tracking-[0.3em] uppercase italic text-dark-muted">Technical Breakdown</h2>
                                        <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10" />
                                    </div>

                                    {comparisonData.length > 0 ? (
                                        <div className="py-2 relative z-10">
                                            <RadarCompareChart data={radarData} subjects={radarSubjects} />
                                        </div>
                                    ) : (
                                        <div className="h-[280px] flex flex-col items-center justify-center text-dark-muted p-10 text-center bg-dark-bg/20 rounded-3xl border border-white/5 border-dashed">
                                            <div className="bg-dark-surface p-6 rounded-3xl mb-6 shadow-inner">
                                                <History size={40} className="opacity-10" />
                                            </div>
                                            <h3 className="text-xs font-black uppercase tracking-[0.2em] mb-2 text-white/40">Insufficient Data Matrix</h3>
                                            <p className="text-[10px] opacity-40 leading-relaxed font-medium italic max-w-[200px]">At least one synchronized log is required per product to establish a technical benchmark.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    {comparisonData.map((p) => (
                                        <div key={p.id} className="card group relative overflow-hidden transition-all duration-500 border-l-[6px] bg-dark-surface/30 backdrop-blur-md" style={{ borderLeftColor: p.color }}>
                                            <div className="absolute top-0 right-0 p-6 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                                                <Sparkles size={80} />
                                            </div>
                                            <div className="mb-6 relative z-10">
                                                <div className="flex items-center justify-between mb-1.5">
                                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-dark-muted">{p.brandName || 'Unlabeled Source'}</p>
                                                    <p className="text-[9px] font-black text-white/20 uppercase tracking-widest">{p.sessionCount} Syncs</p>
                                                </div>
                                                <h3 className="font-black text-xl tracking-tighter group-hover:text-white transition-colors uppercase italic">{p.name}</h3>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4 relative z-10">
                                                <div className="bg-dark-bg/50 backdrop-blur-sm p-4 rounded-2xl border border-white/5 text-center group-hover:border-white/10 transition-colors">
                                                    <Star size={10} className="mx-auto mb-2 text-accent-yellow" />
                                                    <p className="font-black text-2xl leading-none tracking-tighter italic">{p.avgRating.toFixed(1)}</p>
                                                    <p className="text-[7px] font-black uppercase tracking-[0.25em] text-dark-muted mt-2 leading-none">Rating</p>
                                                </div>
                                                <div className="bg-dark-bg/50 backdrop-blur-sm p-4 rounded-2xl border border-white/5 text-center group-hover:border-white/10 transition-colors">
                                                    <Activity size={10} className="mx-auto mb-2 text-accent-purple" />
                                                    <p className="font-black text-2xl leading-none tracking-tighter italic">{Math.round(p.avgValue)}</p>
                                                    <p className="text-[7px] font-black uppercase tracking-[0.25em] text-dark-muted mt-2 leading-none">V-Index</p>
                                                </div>
                                                <div className="bg-dark-bg/50 backdrop-blur-sm p-4 rounded-2xl border border-white/5 text-center group-hover:border-white/10 transition-colors">
                                                    <Clock size={10} className="mx-auto mb-2 text-accent-blue" />
                                                    <p className="font-black text-2xl leading-none tracking-tighter italic">{p.sessionCount}</p>
                                                    <p className="text-[7px] font-black uppercase tracking-[0.25em] text-dark-muted mt-2 leading-none">Depth</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="card text-center py-24 bg-dark-surface/20 border-dashed border-white/10 group cursor-pointer hover:bg-dark-surface/30 hover:border-accent-purple/30 transition-all duration-500" onClick={() => {/* Focus products list */ }}>
                                <div className="inline-flex p-8 rounded-full bg-dark-bg border border-white/5 mb-8 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.1)] transition-all duration-700">
                                    <Swords size={48} className="text-accent-purple/20 group-hover:text-accent-purple/40 transition-colors" />
                                </div>
                                <h2 className="text-lg font-black uppercase tracking-[0.3em] mb-3">Initialize Battle</h2>
                                <p className="text-[10px] text-dark-muted max-w-[240px] mx-auto leading-relaxed font-medium">
                                    Compare products from your shared registry for a technical side-by-side performance matrix.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
